import { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  Search,
  Plus,
  Edit3,
  Trash2,
  Eye,
  Calendar,
  Clock,
  CheckCircle2,
  Archive,
  Tag,
  ExternalLink,
  Copy,
  X,
  Send,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  FirestoreContent,
  onContentChange,
  addContent as firebaseAddContent,
  updateContent as firebaseUpdateContent,
  deleteContent as firebaseDeleteContent,
  publishContent as firebasePublishContent,
  unpublishContent as firebaseUnpublishContent,
} from '@/services/firebase/adminFirestore';
import { generateSlug } from '@/services/contentService';

type ContentType = 'blog' | 'docs' | 'changelog';
type ContentStatus = 'published' | 'draft' | 'archived';

export function AdminContent() {
  // State for content items from Firebase
  const [items, setItems] = useState<FirestoreContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState<FirestoreContent | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    type: 'blog' as ContentType,
    content: '',
    excerpt: '',
    tags: '',
    author: 'Admin',
    authorId: 'usr-1',
  });

  // Set up real-time listener for content changes
  useEffect(() => {
    setLoading(true);
    setError(null);

    const unsubscribe = onContentChange((content) => {
      setItems(content);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const filteredContent = items.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.slug.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || item.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  // Calculate stats from items
  const stats = useMemo(() => {
    const total = items.length;
    const published = items.filter(item => item.status === 'published').length;
    const drafts = items.filter(item => item.status === 'draft').length;
    const totalViews = items.reduce((sum, item) => sum + (item.views || 0), 0);
    const byType = {
      blog: items.filter(item => item.type === 'blog').length,
      docs: items.filter(item => item.type === 'docs').length,
      changelog: items.filter(item => item.type === 'changelog').length,
    };
    return { total, published, drafts, totalViews, byType };
  }, [items]);

  const getStatusBadge = (status: ContentStatus) => {
    switch (status) {
      case 'published':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">
          <CheckCircle2 size={10} /> Published
        </span>;
      case 'draft':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-500">
          <Clock size={10} /> Draft
        </span>;
      case 'archived':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-500/10 text-gray-500">
          <Archive size={10} /> Archived
        </span>;
      default:
        return null;
    }
  };

  const getTypeBadge = (type: ContentType) => {
    const colors = {
      blog: 'bg-purple-500/10 text-purple-500',
      docs: 'bg-blue-500/10 text-blue-500',
      changelog: 'bg-orange-500/10 text-orange-500'
    };
    return (
      <span className={cn('text-xs px-2 py-0.5 rounded-full capitalize', colors[type])}>
        {type}
      </span>
    );
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      type: 'blog',
      content: '',
      excerpt: '',
      tags: '',
      author: 'Admin',
      authorId: 'usr-1',
    });
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) return;

    setActionLoading('create');
    try {
      await firebaseAddContent({
        title: formData.title,
        slug: formData.slug || generateSlug(formData.title),
        type: formData.type,
        status: 'draft',
        content: formData.content,
        excerpt: formData.excerpt,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        author: formData.author,
        authorId: formData.authorId,
      });

      resetForm();
      setShowCreateModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create content');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = (item: FirestoreContent) => {
    setSelectedContent(item);
    setFormData({
      title: item.title,
      slug: item.slug,
      type: item.type,
      content: item.content,
      excerpt: item.excerpt || '',
      tags: item.tags.join(', '),
      author: item.author,
      authorId: item.authorId,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedContent || !formData.title.trim()) return;

    setActionLoading('update');
    try {
      await firebaseUpdateContent(selectedContent.id, {
        title: formData.title,
        slug: formData.slug || generateSlug(formData.title),
        type: formData.type,
        content: formData.content,
        excerpt: formData.excerpt,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      });

      resetForm();
      setSelectedContent(null);
      setShowEditModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update content');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    setActionLoading(`delete-${id}`);
    try {
      await firebaseDeleteContent(id);
      setShowDeleteConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete content');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePublish = async (id: string) => {
    setActionLoading(`publish-${id}`);
    try {
      await firebasePublishContent(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish content');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnpublish = async (id: string) => {
    setActionLoading(`unpublish-${id}`);
    try {
      await firebaseUnpublishContent(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unpublish content');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (id: string) => {
    setActionLoading(`archive-${id}`);
    try {
      await firebaseUpdateContent(id, { status: 'archived' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive content');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopySlug = (slug: string) => {
    navigator.clipboard.writeText(`/${slug}`);
  };

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title)
    }));
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-text-secondary">
          <Loader2 className="animate-spin" size={24} />
          <span>Loading content...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
          <p className="text-red-400 flex-1">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-300"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Content</h1>
          <p className="text-text-secondary">Manage blog posts, documentation, and changelog</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          <Plus size={16} />
          New Content
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-text-primary">{stats.total}</p>
          <p className="text-sm text-text-secondary">Total Content</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-green-500">{stats.published}</p>
          <p className="text-sm text-text-secondary">Published</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-yellow-500">{stats.drafts}</p>
          <p className="text-sm text-text-secondary">Drafts</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-accent">{stats.totalViews.toLocaleString()}</p>
          <p className="text-sm text-text-secondary">Total Views</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-bg-card border border-border-default rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search content..."
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
          >
            <option value="all">All Types</option>
            <option value="blog">Blog Posts ({stats.byType.blog})</option>
            <option value="docs">Documentation ({stats.byType.docs})</option>
            <option value="changelog">Changelog ({stats.byType.changelog})</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Content List */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <div className="divide-y divide-border-default">
          {filteredContent.length === 0 ? (
            <div className="p-8 text-center text-text-tertiary">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>No content found</p>
            </div>
          ) : (
            filteredContent.map((item) => (
              <div
                key={item.id}
                className="p-4 hover:bg-bg-elevated/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-bg-elevated flex items-center justify-center flex-shrink-0">
                      <FileText className="text-text-tertiary" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-text-primary truncate">{item.title}</h3>
                        {getTypeBadge(item.type)}
                        {getStatusBadge(item.status)}
                      </div>
                      <p className="text-sm text-text-secondary line-clamp-1 mb-2">{item.excerpt}</p>
                      <div className="flex items-center gap-4 text-xs text-text-tertiary">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {new Date(item.updatedAt).toLocaleDateString()}
                        </span>
                        <span>{item.author}</span>
                        <span className="flex items-center gap-1">
                          <Eye size={12} />
                          {(item.views || 0).toLocaleString()}
                        </span>
                        <div className="flex items-center gap-1">
                          <Tag size={12} />
                          {item.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="px-1.5 py-0.5 rounded bg-bg-elevated">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {item.status === 'draft' && (
                      <button
                        onClick={() => handlePublish(item.id)}
                        disabled={actionLoading === `publish-${item.id}`}
                        className="p-2 rounded-lg hover:bg-green-500/10 transition-colors text-text-tertiary hover:text-green-500 disabled:opacity-50"
                        title="Publish"
                      >
                        {actionLoading === `publish-${item.id}` ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Send size={16} />
                        )}
                      </button>
                    )}
                    {item.status === 'published' && (
                      <button
                        onClick={() => handleUnpublish(item.id)}
                        disabled={actionLoading === `unpublish-${item.id}`}
                        className="p-2 rounded-lg hover:bg-yellow-500/10 transition-colors text-text-tertiary hover:text-yellow-500 disabled:opacity-50"
                        title="Unpublish"
                      >
                        {actionLoading === `unpublish-${item.id}` ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Clock size={16} />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => window.open(`/${item.slug}`, '_blank')}
                      className="p-2 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-text-primary"
                      title="View"
                    >
                      <ExternalLink size={16} />
                    </button>
                    <button
                      onClick={() => handleCopySlug(item.slug)}
                      className="p-2 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-text-primary"
                      title="Copy slug"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      className="p-2 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-text-primary"
                      title="Edit"
                    >
                      <Edit3 size={16} />
                    </button>
                    {item.status !== 'archived' && (
                      <button
                        onClick={() => handleArchive(item.id)}
                        disabled={actionLoading === `archive-${item.id}`}
                        className="p-2 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-orange-500 disabled:opacity-50"
                        title="Archive"
                      >
                        {actionLoading === `archive-${item.id}` ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Archive size={16} />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => setShowDeleteConfirm(item.id)}
                      className="p-2 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-red-500"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Delete Confirmation */}
                {showDeleteConfirm === item.id && (
                  <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-sm text-red-400 mb-3">
                      Are you sure you want to delete "{item.title}"? This action cannot be undone.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDelete(item.id)}
                        disabled={actionLoading === `delete-${item.id}`}
                        className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                      >
                        {actionLoading === `delete-${item.id}` && (
                          <Loader2 size={14} className="animate-spin" />
                        )}
                        Delete
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(null)}
                        className="px-3 py-1.5 bg-bg-elevated text-text-secondary rounded-lg text-sm hover:text-text-primary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Content Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-border-default flex items-center justify-between">
              <h2 className="text-xl font-semibold">Create New Content</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-text-tertiary hover:text-text-primary">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Enter title..."
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="url-friendly-slug"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as ContentType }))}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                >
                  <option value="blog">Blog Post</option>
                  <option value="docs">Documentation</option>
                  <option value="changelog">Changelog</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Excerpt</label>
                <input
                  type="text"
                  value={formData.excerpt}
                  onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                  placeholder="Brief description..."
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Write your content here (Markdown supported)..."
                  rows={8}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-y"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tags</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="tag1, tag2, tag3"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-border-default flex justify-end gap-3">
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="btn btn-primary flex items-center gap-2"
                disabled={!formData.title.trim() || actionLoading === 'create'}
              >
                {actionLoading === 'create' && <Loader2 size={16} className="animate-spin" />}
                <Plus size={16} />
                Create Draft
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Content Modal */}
      {showEditModal && selectedContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-border-default flex items-center justify-between">
              <h2 className="text-xl font-semibold">Edit Content</h2>
              <button onClick={() => { setShowEditModal(false); setSelectedContent(null); resetForm(); }} className="text-text-tertiary hover:text-text-primary">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter title..."
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="url-friendly-slug"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as ContentType }))}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                >
                  <option value="blog">Blog Post</option>
                  <option value="docs">Documentation</option>
                  <option value="changelog">Changelog</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Excerpt</label>
                <input
                  type="text"
                  value={formData.excerpt}
                  onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                  placeholder="Brief description..."
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Write your content here (Markdown supported)..."
                  rows={8}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-y"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tags</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="tag1, tag2, tag3"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-border-default flex justify-end gap-3">
              <button onClick={() => { setShowEditModal(false); setSelectedContent(null); resetForm(); }} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="btn btn-primary flex items-center gap-2"
                disabled={!formData.title.trim() || actionLoading === 'update'}
              >
                {actionLoading === 'update' && <Loader2 size={16} className="animate-spin" />}
                <CheckCircle2 size={16} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
